import { forwardRef, ReactNode, Ref, useEffect } from "react";
import { CommonInput, IconInputWrapper, InputWrapper } from "./atom";
import SearchIcon from "../../assets/svg/search";
import { useRef, useState } from "react";
import { useClickOutside } from "project-config/hooks/useClickOutside";
export function Input({
  inputType = "normal",
  onSubmit,
  onBlur,
  onChange,
  onClickOutside,
  clickOutsideDependency = [],
  value,
  prefix,
  suffix,
  error,
  handleInputInside,
  className,
  ...props
}: InputProps) {
  const [focus, setFocus] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [hasInput, setHasInput] = useState(false);
  const ref = useRef();
  const handleKeyEnter = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      onSubmit?.(value);
    }
  };
  useClickOutside(
    ref,
    () => {
      setFocus(false);
      onClickOutside?.();
    },
    true,
    [clickOutsideDependency]
  );
  const isIconInput = prefix || suffix;
  const _inputType = isIconInput ? "icon" : inputType;
  const InputAtom = {
    normal: CommonInput,
    search: SearchInput,
    icon: IconInput,
  }[_inputType];

  useEffect(() => {
    if (!handleInputInside) return;
    if (inputValue !== value) {
      const valueAfterChange = onChange?.(inputValue);
      setInputValue(valueAfterChange);
    }
  }, [inputValue, handleInputInside]);

  useEffect(() => {
    if (!handleInputInside) return;
    setInputValue(value);
  }, [value, handleInputInside]);

  return (
    <InputAtom
      ref={ref}
      focus={focus}
      prefix={prefix}
      suffix={suffix}
      onKeyDown={handleKeyEnter}
      onBlur={() => {
        onBlur?.(value, hasInput);
        setHasInput(false);
      }}
      onClick={() => setFocus(true)}
      onFocus={() => setFocus(true)}
      value={handleInputInside ? inputValue : value}
      onChange={(event: React.ChangeEventHandler<HTMLInputElement>) => {
        setHasInput(true);
        if (handleInputInside) {
          setInputValue(event.target.value);
        } else {
          onChange?.(event.target.value);
        }
      }}
      className={`${className} ${error ? "error" : ""}`}
      {...props}
    />
  );
}

export type InputType = "normal" | "search";

export type InputProps =
  | Omit<React.InputHTMLAttributes<HTMLInputElement>, "onBlur" | "onChange">
  | {
      width?: string;
      height?: string;
      color?: string;
      background?: string;
      inputType?: InputType;
      placeholder?: ReactNode;
      placeholderColor?: string;
      borderColor?: string;
      focus?: boolean;
      focusBorderColor?: string;
      style?: React.CSSProperties;
      className?: string;
      clickOutsideDependency?: any[];
      ref?: RefObject<HTMLInputElement>;
      customCSS?: any;
      prefix?: ReactNode;
      suffix?: ReactNode;
      handleInputInside?: boolean;
      error?: boolean;
      onSubmit?: (value: any) => void;
      onBlur?: (value: any, hasInput?: boolean) => void;
      onChange?: (value: any) => any;
      onClickOutside?: () => void;
    };

export const SearchInput = forwardRef<HTMLDivElement, InputProps>(
  (
    { focus, style, background, focusBorderColor, borderColor, ...props },
    ref
  ) => {
    return (
      <InputWrapper
        focus={focus}
        ref={ref}
        style={style}
        focusBorderColor={focusBorderColor}
        background={background}
        borderColor={borderColor}
        className="search-input-wrapper"
      >
        <SearchIcon />
        <CommonInput background={background} {...props} />
      </InputWrapper>
    );
  }
);

export const IconInput = forwardRef<HTMLDivElement, InputProps>(
  (
    {
      focus,
      style,
      background,
      focusBorderColor,
      borderColor,
      prefix,
      suffix,
      ...props
    },
    ref
  ) => {
    return (
      <IconInputWrapper
        focus={focus}
        ref={ref}
        style={style}
        focusBorderColor={focusBorderColor}
        background={background}
        borderColor={borderColor}
        className="icon-input-wrapper"
        prefix={prefix}
        suffix={suffix}
      >
        {prefix}
        <CommonInput background={background} {...props} />
        {suffix}
      </IconInputWrapper>
    );
  }
);
